import { z } from 'zod';

import { runtimeConfig } from '@/config/runtime-config';
import { clearToken, readToken } from '@/lib/session/token-store';

import { ApiError, ContractError, SessionExpiredError } from './errors';

// Two error shapes exist on the wire (see docs/api.md). Both carry a human-readable title.
const errorBodySchema = z.union([
  z.object({ code: z.number(), message_code: z.string(), title: z.string() }),
  z.object({ httpStatusCode: z.number(), messageCode: z.string(), title: z.string() }),
]);

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Sent as JSON unless it is FormData. */
  body?: unknown;
  /** Bearer token attached when true. Default true. */
  auth?: boolean;
  /** Query string, undefined values dropped; repeated keys allowed via arrays. */
  query?: Record<string, string | readonly string[] | undefined>;
  schema: z.ZodType<T>;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions<T>): Promise<T> {
  const { method = 'GET', body, auth = true, query, schema, signal } = options;
  const url = new URL(`${runtimeConfig().API_BASE_URL}/api${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    for (const v of typeof value === 'string' ? [value] : value) url.searchParams.append(key, v);
  }

  const headers = new Headers({ Accept: 'application/json' });
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
    payload = JSON.stringify(body);
  }
  if (auth) {
    const token = readToken();
    if (!token) throw new SessionExpiredError();
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, {
    method,
    headers,
    body: payload ?? null,
    signal: signal ?? null,
  });
  const text = await response.text();
  const json: unknown = text ? safeJson(text) : undefined;

  if (!response.ok) throw toApiError(response.status, json);

  const parsed = schema.safeParse(json);
  if (!parsed.success) throw new ContractError(path, z.prettifyError(parsed.error));
  return parsed.data;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function toApiError(status: number, json: unknown): ApiError {
  const parsed = errorBodySchema.safeParse(json);
  if (!parsed.success)
    return new ApiError(status, 'unknown', 'Something went wrong. Please try again.');
  const code = 'code' in parsed.data ? parsed.data.message_code : parsed.data.messageCode;
  if (status === 406 && code === 'invalid_or_expired_jwt') {
    clearToken();
    return new SessionExpiredError();
  }
  return new ApiError(status, code, parsed.data.title);
}
